// @flow

import React, { type Node, useMemo } from 'react';
import { useUniqueId } from '@tectonics/hooks';
import { InputMask, formatValueWithMask } from '@tectonics/components/input-mask';
import { type CardBrand, detectCardBrand } from '@tectonics/prs/card-brand';
import { useUserInputValue, useUserInputAsTextFieldWithFormatting } from '@tectonics/prs/user-input-values';
import { useSubmitting } from '@tectonics/prs/submitting';
import {
  useUserInputValidationError,
  type UserInputValidationError,
  serverErrorMessage,
} from '@tectonics/prs/validation';
import { useFormHasBeenSent } from '@tectonics/prs/form-status';
import type { Country } from '@tectonics/prs/country';
import { getCountry, useIncomingPayment } from '@tectonics/prs/incoming-payment';

import { Field } from '../../../molecules';
import { Input, Empty, Label } from '../../../atoms';

type SecurityCodeMask = ImmutableArray<RegExp | string>;

export function FieldCardSecurityCode(): Node {
  const incomingPayment = useIncomingPayment();
  const country = getCountry(incomingPayment);

  const [cardNumber] = useUserInputValue('cardNumber');
  const inputId = useUniqueId();
  const brand = useMemo(() => detectCardBrand(cardNumber), [cardNumber]);
  const mask = getSecurityCodeMask(brand);
  const submitting = useSubmitting();

  const [value, handleChange] = useUserInputAsTextFieldWithFormatting(
    'cardSecurityCode',
    formatRaw(mask),
    parseFormatted
  );

  const labelMessage = useLabelMessage();
  const formHasBeenSent = useFormHasBeenSent();
  const validationError = useUserInputValidationError('cardSecurityCode');

  return (
    <Field
      size="half"
      data-test-name="card-security-code"
      label={<Label htmlFor={inputId}>{labelMessage}</Label>}
      showFloatingLabel={!!value}
      error={<ValidationErrorMessage validationError={validationError} />}
      hasError={validationError && formHasBeenSent}
      tooltip={getTooltipText(country)}
    >
      <Input
        as={InputMask}
        data-secret
        mask={mask}
        id={inputId}
        onChange={handleChange}
        placeholder={labelMessage}
        readOnly={submitting}
        type="tel"
        value={value}
        name="card-security-code"
      />
    </Field>
  );
}

function getTooltipText(country: Country): string {
  switch (country) {
    case 'br':
      return 'O CVV ou CVC é um código de com 3 dígitos encontrado na parte de trás do seu cartão. No caso dos cartões American Express, o CVV é de 4 dígitos e está localizado na frente.';
    case 'ar':
    case 'cl':
    case 'co':
    case 'mx':
    case 'uy':
    case 'py':
      return 'CVV o CVC es un código de 3 dígitos que se encuentra en la parte de atrás de su tarjeta. En tarjetas de American Express, el CVV es de 4 dígitos y se encuentra al frente de la tarjeta.';
    default:
      throw new Error(`Translation not found for "${country}"`);
  }
}

function useLabelMessage(): string {
  const incomingPayment = useIncomingPayment();
  const country = getCountry(incomingPayment);

  switch (country) {
    case 'br':
    case 'ar':
    case 'bo':
    case 'cl':
    case 'co':
    case 'ec':
    case 'mx':
    case 'pe':
    case 'uy':
    case 'py':
    case 'za':
      return 'CVV';
    default:
      throw new Error(`No message found for field card cvv in country ${country}`);
  }
}

type ValidationErrorMessageProps = {|
  validationError: ?UserInputValidationError,
|};

function ValidationErrorMessage({ validationError }: ValidationErrorMessageProps): Node {
  const incomingPayment = useIncomingPayment();
  const country = getCountry(incomingPayment);

  if (!validationError) {
    return <Empty />;
  }

  const message = getErrorMessages(validationError)[country];

  if (message) {
    return message;
  }

  return serverErrorMessage(validationError.payload);
}

function getErrorMessages({ id }: UserInputValidationError): { [Country]: string } {
  switch (id) {
    case 'empty':
      return {
        br: 'Preencha o código de segurança do cartão',
        mx: 'Completa el código de seguridad de la tarjeta',
        ar: 'Completa el código de seguridad de la tarjeta',
        co: 'Completa el código de seguridad de la tarjeta',
        cl: 'Completa el código de seguridad de la tarjeta',
        pe: 'Completa el código de seguridad de la tarjeta',
        ec: 'Completa el código de seguridad de la tarjeta',
        bo: 'Completa el código de seguridad de la tarjeta',
        uy: 'Completa el código de seguridad de la tarjeta',
        za: 'Fill in the card\'s security code',
      };

    case 'invalid-pattern':
      return {
        br: 'Código de segurança inválido',
        mx: 'El CVV informado es inválido.',
        ar: 'El CVV informado es inválido.',
        co: 'El CVV informado es inválido.',
        cl: 'El CVV informado es inválido.',
        pe: 'El CVV informado es inválido.',
        ec: 'El CVV informado es inválido.',
        bo: 'El CVV informado es inválido.',
        uy: 'El CVV informado es inválido.',
        za: 'Invalid security code.',
      };

    default:
      return {
        br: 'CVV inválido',
        mx: 'El CVV informado es inválido.',
        ar: 'El CVV informado es inválido.',
        co: 'El CVV informado es inválido.',
        cl: 'El CVV informado es inválido.',
        pe: 'El CVV informado es inválido.',
        ec: 'El CVV informado es inválido.',
        bo: 'El CVV informado es inválido.',
        uy: 'El CVV informado es inválido.',
        za: 'Invalid CVV.',
      };
  }
}

function formatRaw(mask: SecurityCodeMask): (rawValue: string) => string {
  return (rawValue: string): string => formatValueWithMask(rawValue, mask);
}

function parseFormatted(formattedValue: string): string {
  return formattedValue.replace(/\D/gu, '');
}

function getSecurityCodeMask(cardBrand: ?CardBrand): SecurityCodeMask {
  switch (cardBrand) {
    case 'amex':
      return AMEX_MASK;
    default:
      return DEFAULT_MASK;
  }
}

const AMEX_MASK = [/[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/];
const DEFAULT_MASK = [/[0-9]/, /[0-9]/, /[0-9]/];
